import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardHeader,
  Alert,
  CircularProgress,
  TextField,
  Stack,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
  Chip,
  FormControl,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  Description as DescriptionIcon2,
  Folder as FolderIcon,
} from '@mui/icons-material';
import AlertItem from '../../Components/AlertItem';
import {
  NewsFeedCreateRequest,
  NewsFeedCreateSection,
  NewsFeedCreateContent,
  NewsFeedCreateListItem,
} from '../../types/newsfeed';
import { createNewsfeedDetail } from '../../endpoints/newsfeedTs';
import { hasPermission } from '../../utils/permissions';
import DateFilter from '../../Components/DateFilter';
import ChapterSelector from '../../Components/ChapterSelector';
import { useApiSend } from '../../utils/httpClientTs';
import { getDocumentList, getBundleList } from '../../endpoints/documents';
import { useApiGet } from '../../utils/httpClientTs';
import dayjs from 'dayjs';

// Content type IDs
const CONTENT_TYPES = {
  paragraph: '60308536096c4ce088bee75af7ae8ae2',
  list: 'c1e257bc8c674ae28a7b2085518aea9e',
  video: 'fd9dd86a20e24e79b920e191f3b8203a',
  document: 'f0f8caf2e4934b7e86ee63a98b9b08ea', // For library documents
  bundle: '8cf9d8284bfa481087b0b7a76b690e8c', // For library bundles
};

export default function NewsfeedCreate(): React.JSX.Element {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewsFeedCreateRequest>({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    sections: [
      {
        title: 'Introduction',
        order: 1,
        content: [
          {
            content_type_id: CONTENT_TYPES.paragraph,
            title: null,
            list: null,
            paragraph: '',
            order: 1,
            video_url: null,
            document_id: null,
            bundle_id: null,
          },
        ],
      },
    ],
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    is_active: true,
    local_chapter_id: null,
    spd_chapter_id: null,
    is_international: true,
  });

  // Check permissions
  const canCreate = hasPermission('NEWSFEED_CREATE');

  // Modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<{
    sectionIndex: number;
    contentIndex: number;
  } | null>(null);
  const [documentSearch, setDocumentSearch] = useState('');
  const [bundleSearch, setBundleSearch] = useState('');

  // API mutation hook using useApiSend
  const { mutate: createNewsfeed, isPending: isSaving } = useApiSend(
    createNewsfeedDetail,
    (data) => {
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/newsfeed');
      }, 2000);
    },
    (error: any) => {
      const errorMessage =
        error?.message ||
        error?.error ||
        'Failed to create newsfeed. Please try again.';
      setError(errorMessage);
      console.error('Error creating newsfeed:', error);
    }
  );

  // API hooks for documents and bundles
  const { data: documentsData, refetch: refetchDocuments } = useApiGet(
    ['documents', documentSearch],
    () => getDocumentList({ search: documentSearch, page: 1, per_page: 20 }),
    { enabled: showDocumentModal }
  );

  const { data: bundlesData, refetch: refetchBundles } = useApiGet(
    ['bundles', bundleSearch],
    () => getBundleList(bundleSearch),
    { enabled: showBundleModal }
  );

  const handleInputChange = (
    field: keyof NewsFeedCreateRequest,
    value: string | null | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts making changes
    if (error) {
      setError(null);
    }
  };

  const handleDateChange = (field: 'start_date' | 'end_date', value: any) => {
    if (value) {
      setFormData((prev) => ({ ...prev, [field]: value.format('YYYY-MM-DD') }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: null }));
    }
    // Clear error when user starts making changes
    if (error) {
      setError(null);
    }
  };

  const handleChapterSelect = (
    field: 'local_chapter_id' | 'spd_chapter_id',
    value: string | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts making changes
    if (error) {
      setError(null);
    }
  };

  const handleSectionTitleChange = (sectionIndex: number, title: string) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, index) =>
        index === sectionIndex ? { ...section, title } : section
      ),
    }));
    // Clear error when user starts making changes
    if (error) {
      setError(null);
    }
  };

  const handleContentChange = (
    sectionIndex: number,
    contentIndex: number,
    field: string,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, sIndex) =>
        sIndex === sectionIndex
          ? {
              ...section,
              content: section.content.map((content, cIndex) =>
                cIndex === contentIndex
                  ? { ...content, [field]: value }
                  : content
              ),
            }
          : section
      ),
    }));
    // Clear error when user starts making changes
    if (error) {
      setError(null);
    }
  };

  const addSection = () => {
    const newSection: NewsFeedCreateSection = {
      title: `Section ${formData.sections.length + 1}`,
      order: formData.sections.length + 1,
      content: [
        {
          content_type_id: CONTENT_TYPES.paragraph,
          title: null,
          list: null,
          paragraph: '',
          order: 1,
          video_url: null,
          document_id: null,
          bundle_id: null,
        },
      ],
    };

    setFormData((prev) => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
  };

  const removeSection = (sectionIndex: number) => {
    if (formData.sections.length > 1) {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.filter((_, index) => index !== sectionIndex),
      }));
    }
  };

  const addContent = (
    sectionIndex: number,
    contentType: 'paragraph' | 'list' | 'video' | 'document' | 'bundle'
  ) => {
    const section = formData.sections[sectionIndex];
    const newContent: NewsFeedCreateContent = {
      content_type_id: CONTENT_TYPES[contentType],
      title: contentType === 'list' ? 'New List' : null,
      list:
        contentType === 'list'
          ? [{ content: '', order: 1, is_link: false, link_url: null }]
          : null,
      paragraph: contentType === 'paragraph' ? '' : null,
      order: section.content.length + 1,
      video_url: contentType === 'video' ? '' : null,
      document_id: contentType === 'document' ? null : null,
      bundle_id: contentType === 'bundle' ? null : null,
    };

    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, index) =>
        index === sectionIndex
          ? { ...section, content: [...section.content, newContent] }
          : section
      ),
    }));
  };

  const removeContent = (sectionIndex: number, contentIndex: number) => {
    const section = formData.sections[sectionIndex];
    if (section.content.length > 1) {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, sIndex) =>
          sIndex === sectionIndex
            ? {
                ...section,
                content: section.content.filter(
                  (_, cIndex) => cIndex !== contentIndex
                ),
              }
            : section
        ),
      }));
    }
  };

  const addListItem = (sectionIndex: number, contentIndex: number) => {
    const content = formData.sections[sectionIndex].content[contentIndex];
    if (content.list) {
      const newItem: NewsFeedCreateListItem = {
        content: '',
        order: content.list.length + 1,
        is_link: false,
        link_url: null,
      };

      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, sIndex) =>
          sIndex === sectionIndex
            ? {
                ...section,
                content: section.content.map((content, cIndex) =>
                  cIndex === contentIndex
                    ? { ...content, list: [...content.list!, newItem] }
                    : content
                ),
              }
            : section
        ),
      }));
    }
  };

  const removeListItem = (
    sectionIndex: number,
    contentIndex: number,
    itemIndex: number
  ) => {
    const content = formData.sections[sectionIndex].content[contentIndex];
    if (content.list && content.list.length > 1) {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, sIndex) =>
          sIndex === sectionIndex
            ? {
                ...section,
                content: section.content.map((content, cIndex) =>
                  cIndex === contentIndex
                    ? {
                        ...content,
                        list:
                          content.list?.filter(
                            (_, iIndex) => iIndex !== itemIndex
                          ) || [],
                      }
                    : content
                ),
              }
            : section
        ),
      }));
    }
  };

  const handleListItemChange = (
    sectionIndex: number,
    contentIndex: number,
    itemIndex: number,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, sIndex) =>
        sIndex === sectionIndex
          ? {
              ...section,
              content: section.content.map((content, cIndex) =>
                cIndex === contentIndex
                  ? {
                      ...content,
                      list:
                        content.list?.map((item, iIndex) =>
                          iIndex === itemIndex
                            ? { ...item, content: value }
                            : item
                        ) || [],
                    }
                  : content
              ),
            }
          : section
      ),
    }));
  };

  const handleListItemLinkChange = (
    sectionIndex: number,
    contentIndex: number,
    itemIndex: number,
    field: 'is_link' | 'link_url',
    value: boolean | string
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, sIndex) =>
        sIndex === sectionIndex
          ? {
              ...section,
              content: section.content.map((content, cIndex) =>
                cIndex === contentIndex
                  ? {
                      ...content,
                      list:
                        content.list?.map((item, iIndex) =>
                          iIndex === itemIndex
                            ? { ...item, [field]: value }
                            : item
                        ) || [],
                    }
                  : content
              ),
            }
          : section
      ),
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) return false;
    if (!formData.description?.trim()) return false;
    if (!formData.start_date) return false;
    if (!formData.end_date) return false;

    // Validate that end date is after start date
    if (dayjs(formData.end_date).isBefore(dayjs(formData.start_date))) {
      return false;
    }

    // Check if all sections have content
    for (const section of formData.sections) {
      if (!section.title?.trim()) return false;
      for (const content of section.content) {
        if (
          content.content_type_id === CONTENT_TYPES.paragraph &&
          !content.paragraph?.trim()
        )
          return false;
        if (
          content.content_type_id === CONTENT_TYPES.list &&
          (!content.title?.trim() ||
            !content.list?.some((item) => item.content.trim()))
        )
          return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    setError(null);

    // Use the API mutation hook
    createNewsfeed(formData);
  };

  const handleDiscard = () => {
    setShowDiscardDialog(true);
  };

  const confirmDiscard = () => {
    setShowDiscardDialog(false);
    navigate('/newsfeed');
  };

  // Modal handlers
  const openDocumentModal = (sectionIndex: number, contentIndex: number) => {
    setSelectedContent({ sectionIndex, contentIndex });
    setDocumentSearch('');
    setShowDocumentModal(true);
  };

  const openBundleModal = (sectionIndex: number, contentIndex: number) => {
    setSelectedContent({ sectionIndex, contentIndex });
    setBundleSearch('');
    setShowBundleModal(true);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedContent(null);
    setDocumentSearch('');
  };

  const closeBundleModal = () => {
    setShowBundleModal(false);
    setSelectedContent(null);
    setBundleSearch('');
  };

  const selectDocument = (documentId: string) => {
    if (selectedContent) {
      const { sectionIndex, contentIndex } = selectedContent;
      handleContentChange(
        sectionIndex,
        contentIndex,
        'document_id',
        documentId
      );
      closeDocumentModal();
    }
  };

  const selectBundle = (bundleId: string) => {
    if (selectedContent) {
      const { sectionIndex, contentIndex } = selectedContent;
      handleContentChange(sectionIndex, contentIndex, 'bundle_id', bundleId);
      closeBundleModal();
    }
  };

  const handleDocumentSearch = (searchTerm: string) => {
    setDocumentSearch(searchTerm);
    // Debounce the search
    setTimeout(() => {
      refetchDocuments();
    }, 300);
  };

  const handleBundleSearch = (searchTerm: string) => {
    setBundleSearch(searchTerm);
    // Debounce the search
    setTimeout(() => {
      refetchBundles();
    }, 300);
  };

  if (!canCreate) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="You don't have permission to create newsfeeds. Please contact an administrator."
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/newsfeed')}
          sx={{ cursor: 'pointer' }}
        >
          Newsfeeds
        </Link>
        <Typography color="text.primary">Create Newsfeed</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardHeader
          title={
            <Typography variant="h4" component="h1" color="primary">
              Create New Newsfeed
            </Typography>
          }
          subheader={
            <Typography variant="body1" color="text.secondary">
              Build a comprehensive newsfeed with multiple sections and rich
              content
            </Typography>
          }
          action={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/newsfeed')}
                size="large"
              >
                Back to List
              </Button>
            </Stack>
          }
        />
      </Card>

      {/* Success Alert */}
      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Newsfeed created successfully! Redirecting to newsfeed list...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Box sx={{ mb: 3 }}>
          <AlertItem
            severity="error"
            description={error}
            onClose={() => setError(null)}
            keepOpen={true}
          />
        </Box>
      )}

      {/* Main Form */}
      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TitleIcon color="primary" />
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter newsfeed title"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description || ''}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  placeholder="Enter newsfeed description"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={formData.image_url || ''}
                  onChange={(e) =>
                    handleInputChange('image_url', e.target.value)
                  }
                  placeholder="https://example.com/image.jpg"
                  InputProps={{
                    startAdornment: (
                      <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Link URL"
                  value={formData.link_url || ''}
                  onChange={(e) =>
                    handleInputChange('link_url', e.target.value)
                  }
                  placeholder="https://example.com"
                  InputProps={{
                    startAdornment: (
                      <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Newsfeed Settings */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Newsfeed Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateFilter
                  disabled={false}
                  filterValue={formData.start_date}
                  updateFilters={(value) =>
                    handleDateChange('start_date', value)
                  }
                  label="Start Date"
                  required={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateFilter
                  disabled={false}
                  filterValue={formData.end_date}
                  updateFilters={(value) => handleDateChange('end_date', value)}
                  label="End Date"
                  required={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) =>
                          handleInputChange('is_active', e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_international}
                        onChange={(e) =>
                          handleInputChange(
                            'is_international',
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    }
                    label="International"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={formData.local_chapter_id}
                  handleChapterSelect={(value) =>
                    handleChapterSelect('local_chapter_id', value)
                  }
                  spd={null}
                  keyId="newsfeed-create"
                  label="Local Chapter"
                  all={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={formData.spd_chapter_id}
                  handleChapterSelect={(value) =>
                    handleChapterSelect('spd_chapter_id', value)
                  }
                  spd={null}
                  keyId="newsfeed-create"
                  label="SPD Chapter"
                  all={true}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Content Sections */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <DescriptionIcon color="primary" />
                Content Sections
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addSection}
                size="small"
              >
                Add Section
              </Button>
            </Box>

            {formData.sections.map((section, sectionIndex) => (
              <Box
                key={sectionIndex}
                sx={{
                  mb: 4,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Section Title"
                    value={section.title || ''}
                    onChange={(e) =>
                      handleSectionTitleChange(sectionIndex, e.target.value)
                    }
                    placeholder="Enter section title"
                    required
                    sx={{ mr: 2 }}
                  />
                  {formData.sections.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeSection(sectionIndex)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Section Content */}
                {section.content.map((content, contentIndex) => (
                  <Box
                    key={contentIndex}
                    sx={{ mb: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Chip
                        label={
                          content.content_type_id === CONTENT_TYPES.paragraph
                            ? 'Paragraph'
                            : content.content_type_id === CONTENT_TYPES.list
                              ? 'List'
                              : content.content_type_id === CONTENT_TYPES.video
                                ? 'Video'
                                : content.content_type_id ===
                                    CONTENT_TYPES.document
                                  ? 'Document'
                                  : content.content_type_id ===
                                      CONTENT_TYPES.bundle
                                    ? 'Bundle'
                                    : 'Unknown'
                        }
                        color={
                          content.content_type_id === CONTENT_TYPES.paragraph
                            ? 'primary'
                            : content.content_type_id === CONTENT_TYPES.list
                              ? 'secondary'
                              : content.content_type_id === CONTENT_TYPES.video
                                ? 'info'
                                : content.content_type_id ===
                                    CONTENT_TYPES.document
                                  ? 'success'
                                  : content.content_type_id ===
                                      CONTENT_TYPES.bundle
                                    ? 'warning'
                                    : 'default'
                        }
                        size="small"
                      />

                      {section.content.length > 1 && (
                        <IconButton
                          color="error"
                          onClick={() =>
                            removeContent(sectionIndex, contentIndex)
                          }
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    {content.content_type_id === CONTENT_TYPES.paragraph ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Paragraph Content"
                        value={content.paragraph || ''}
                        onChange={(e) =>
                          handleContentChange(
                            sectionIndex,
                            contentIndex,
                            'paragraph',
                            e.target.value
                          )
                        }
                        placeholder="Enter paragraph content"
                        required
                      />
                    ) : content.content_type_id === CONTENT_TYPES.list ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="List Title"
                          value={content.title || ''}
                          onChange={(e) =>
                            handleContentChange(
                              sectionIndex,
                              contentIndex,
                              'title',
                              e.target.value
                            )
                          }
                          placeholder="Enter list title"
                          required
                          sx={{ mb: 2 }}
                        />
                        {content.list?.map((item, itemIndex) => (
                          <Box
                            key={itemIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              label={`List Item ${itemIndex + 1}`}
                              value={item.content}
                              onChange={(e) =>
                                handleListItemChange(
                                  sectionIndex,
                                  contentIndex,
                                  itemIndex,
                                  e.target.value
                                )
                              }
                              placeholder="Enter list item content"
                              required
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={item.is_link}
                                  onChange={(e) =>
                                    handleListItemLinkChange(
                                      sectionIndex,
                                      contentIndex,
                                      itemIndex,
                                      'is_link',
                                      e.target.checked
                                    )
                                  }
                                  color="primary"
                                />
                              }
                              label="Link"
                            />
                            {item.is_link && (
                              <TextField
                                fullWidth
                                label="Link URL"
                                value={item.link_url || ''}
                                onChange={(e) =>
                                  handleListItemLinkChange(
                                    sectionIndex,
                                    contentIndex,
                                    itemIndex,
                                    'link_url',
                                    e.target.value
                                  )
                                }
                                placeholder="https://example.com"
                                InputProps={{
                                  startAdornment: (
                                    <LinkIcon
                                      sx={{ mr: 1, color: 'text.secondary' }}
                                    />
                                  ),
                                }}
                              />
                            )}
                            {content.list && content.list.length > 1 && (
                              <IconButton
                                color="error"
                                onClick={() =>
                                  removeListItem(
                                    sectionIndex,
                                    contentIndex,
                                    itemIndex
                                  )
                                }
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            addListItem(sectionIndex, contentIndex)
                          }
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          Add List Item
                        </Button>
                      </Box>
                    ) : content.content_type_id === CONTENT_TYPES.video ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Video URL"
                          value={content.video_url || ''}
                          onChange={(e) =>
                            handleContentChange(
                              sectionIndex,
                              contentIndex,
                              'video_url',
                              e.target.value
                            )
                          }
                          placeholder="https://www.youtube.com/watch?v=..."
                          InputProps={{
                            startAdornment: (
                              <LinkIcon
                                sx={{ mr: 1, color: 'text.secondary' }}
                              />
                            ),
                          }}
                        />
                      </Box>
                    ) : content.content_type_id === CONTENT_TYPES.document ? (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Document from Library
                        </Typography>
                        <TextField
                          fullWidth
                          label="Content Title (Optional)"
                          value={content.title || ''}
                          onChange={(e) =>
                            handleContentChange(
                              sectionIndex,
                              contentIndex,
                              'title',
                              e.target.value
                            )
                          }
                          variant="outlined"
                          size="small"
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="body1">
                          {content.document_id
                            ? documentsData?.data?.find(
                                (doc) => doc.id === content.document_id
                              )?.name || `Document ID: ${content.document_id}`
                            : 'No document selected'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              openDocumentModal(sectionIndex, contentIndex)
                            }
                            size="small"
                          >
                            Select Document
                          </Button>
                          {content.document_id && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() =>
                                handleContentChange(
                                  sectionIndex,
                                  contentIndex,
                                  'document_id',
                                  null
                                )
                              }
                              size="small"
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : content.content_type_id === CONTENT_TYPES.bundle ? (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Bundle from Library
                        </Typography>
                        <TextField
                          fullWidth
                          label="Content Title (Optional)"
                          value={content.title || ''}
                          onChange={(e) =>
                            handleContentChange(
                              sectionIndex,
                              contentIndex,
                              'title',
                              e.target.value
                            )
                          }
                          variant="outlined"
                          size="small"
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="body1">
                          {content.bundle_id
                            ? bundlesData?.find(
                                (bundle) => bundle.id === content.bundle_id
                              )?.name || `Bundle ID: ${content.bundle_id}`
                            : 'No bundle selected'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              openBundleModal(sectionIndex, contentIndex)
                            }
                            size="small"
                          >
                            Select Bundle
                          </Button>
                          {content.bundle_id && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() =>
                                handleContentChange(
                                  sectionIndex,
                                  contentIndex,
                                  'bundle_id',
                                  null
                                )
                              }
                              size="small"
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                ))}

                {/* Add Content Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'paragraph')}
                    size="small"
                  >
                    Add Paragraph
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'list')}
                    size="small"
                  >
                    Add List
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'video')}
                    size="small"
                  >
                    Add Video
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'document')}
                    size="small"
                  >
                    Add Document
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'bundle')}
                    size="small"
                  >
                    Add Bundle
                  </Button>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Right Column - Actions & Preview */}
        <Grid item xs={12} lg={4}>
          {/* Form Actions */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Stack spacing={2}>
              <Button
                variant="contained"
                startIcon={
                  isSaving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                onClick={handleSave}
                disabled={isSaving || !validateForm()}
                fullWidth
                size="large"
              >
                {isSaving ? 'Creating...' : 'Create Newsfeed'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleDiscard}
                disabled={isSaving}
                fullWidth
                size="large"
              >
                Discard Changes
              </Button>
            </Stack>
          </Paper>

          {/* Preview */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Title
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {formData.title || 'No title entered'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1">
                  {formData.description || 'No description entered'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Sections
                </Typography>
                <Typography variant="body1">
                  {formData.sections.length} section
                  {formData.sections.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Start Date
                </Typography>
                <Typography variant="body1">
                  {formData.start_date
                    ? dayjs(formData.start_date).format('MM/DD/YYYY')
                    : 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  End Date
                </Typography>
                <Typography variant="body1">
                  {formData.end_date
                    ? dayjs(formData.end_date).format('MM/DD/YYYY')
                    : 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={formData.is_active ? 'Active' : 'Inactive'}
                  color={formData.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scope
                </Typography>
                <Chip
                  label={
                    formData.is_international
                      ? 'International'
                      : 'Chapter Specific'
                  }
                  color={formData.is_international ? 'primary' : 'secondary'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date Range
                </Typography>
                <Typography variant="body1">
                  {formData.start_date} to {formData.end_date}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={formData.is_active ? 'Active' : 'Inactive'}
                  color={formData.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scope
                </Typography>
                <Typography variant="body1">
                  {formData.is_international ? 'International' : 'Local/SPD'}
                </Typography>
              </Box>
              {!formData.is_international &&
                (formData.local_chapter_id || formData.spd_chapter_id) && (
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Chapters
                    </Typography>
                    <Typography variant="body1">
                      {formData.local_chapter_id && 'Local Chapter Selected'}
                      {formData.local_chapter_id &&
                        formData.spd_chapter_id &&
                        ' & '}
                      {formData.spd_chapter_id && 'SPD Chapter Selected'}
                    </Typography>
                  </Box>
                )}
              {formData.image_url && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Preview Image
                  </Typography>
                  <img
                    src={formData.image_url}
                    alt="Newsfeed preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Discard Confirmation Dialog */}
      <Dialog
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
      >
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to discard all changes? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Cancel</Button>
          <Button onClick={confirmDiscard} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Selection Modal */}
      <Dialog
        open={showDocumentModal}
        onClose={closeDocumentModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Document from Library</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search documents..."
              value={documentSearch}
              onChange={(e) => handleDocumentSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          {documentsData?.data ? (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {documentsData.data.map((document) => (
                <ListItem
                  key={document.id}
                  button
                  onClick={() => selectDocument(document.id)}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <ListItemIcon>
                    <DescriptionIcon2 color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={document.name}
                    secondary={document.description || 'No description'}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {documentSearch
                  ? 'No documents found'
                  : 'Search for documents to select'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDocumentModal}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Selection Modal */}
      <Dialog
        open={showBundleModal}
        onClose={closeBundleModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Bundle from Library</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search bundles..."
              value={bundleSearch}
              onChange={(e) => handleBundleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          {bundlesData ? (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {bundlesData.map((bundle) => (
                <ListItem
                  key={bundle.id}
                  button
                  onClick={() => selectBundle(bundle.id)}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <ListItemIcon>
                    <FolderIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={bundle.name}
                    secondary={bundle.description || 'No description'}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {bundleSearch
                  ? 'No bundles found'
                  : 'Search for bundles to select'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBundleModal}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
